import { MixedCardItem } from '../../Teams/DataboxItems'
import {
  extractOriginalDataSourceIds,
  type OriginalDataSourcesByMcp
} from '../utils/mcpServerUtils'

export { extractOriginalDataSourceIds } from '../utils/mcpServerUtils'

// TypeScript interfaces for agent MCP servers and scopes
export interface DataProductScope {
  id: string
}

export interface AgentMcpServer {
  agentId?: string
  mcpServerId: string
  teamIdentifier?: string
  dataProductScopes?: DataProductScope[]
  dataProductGroups?: Array<{ databoxId: string | null; dataProductIds: string[] }>
  // Ids of databoxes selected in their entirety - fetch each databox's data products and merge them into
  // scopeConfiguration.dataProductIds
  fullySelectedDataboxIds?: string[]
}

/**
 * Convert databoxes and data products to MixedCardItem[] format for UI display
 * @param databoxes - Array of databox objects from API
 * @param dataProducts - Array of data product objects from API
 * @returns Array of MixedCardItem for UI display
 */
export const parseToMixedItems = (
  databoxes: Array<{
    id: string
    smartDocsDataboxId?: string
    name: string
    filesCount: number
    dataProductsCount: number
    totalSizeFormatted?: string
    owner: string
    createdAt?: string
    teamId?: string
    scopeId?: string
    selectedItems?: any[]
  }>,
  dataProducts: Array<{
    id: string
    name: string
    owners?: string[]
    metadataSource: string
    description?: string
    tier?: string
    securityClassification?: string
  }>
): MixedCardItem[] => {
  const databoxItems: MixedCardItem[] = databoxes.map((db) => ({
    id: db.id,
    smartDocsDataboxId: db.smartDocsDataboxId,
    name: db.name,
    type: 'databox' as const,
    owner: db.owner,
    dateAdded: new Date(db?.createdAt || '').toISOString(),
    source: 'smartDocs',
    filesCount: db.filesCount,
    dataProductsCount: db.dataProductsCount,
    totalSizeFormatted: db.totalSizeFormatted,
    teamIdentifier: db.teamId || db.scopeId || '',
    scopeId: db.scopeId || '',
    ...(db.selectedItems && { selectedItems: db.selectedItems })
  }))

  const dataProductItems: MixedCardItem[] = dataProducts.map((dp) => ({
    type: 'dataproduct' as const,
    name: dp.name,
    id: dp.id,
    owner: dp.owners?.join(', ') ?? '',
    dateAdded: '',
    tier: dp?.tier,
    securityClassification: dp?.securityClassification,
    source: dp?.metadataSource,
    description: dp?.description,
    teamIdentifier: ''
  }))

  return [...databoxItems, ...dataProductItems]
}

/**
 * Build agentMcpServers payload from UI selections for API submission with change detection
 * Note: All IDs are normalized to strings to handle inconsistent API response types (e.g., '54' vs 54)
 * @param mixedAssetData - Array of selected databoxes and data products from UI (current state)
 * @param mcpServerId - The ID of the MCP server (typically Snowflake)
 * @param originalDataSourcesByMcp - Original data source IDs grouped by MCP server (for edit mode)
 * @param agentId - The ID of the agent (optional for new agents)
 * @param includeDataProducts - Whether to build data product scopes/groups for this server
 * @param includeDataBoxTracking - Whether to track databox changes and teamIdentifier for this
 * server. Databox scope itself is never part of this payload - it's carried entirely via the
 * OData `filter` attached separately for servers that need it.
 * @returns AgentMcpServer object for API payload or null if no changes
 */
export const buildAgentMcpServersPayload = (
  mixedAssetData: MixedCardItem[],
  mcpServerId: string,
  originalDataSourcesByMcp: OriginalDataSourcesByMcp | null,
  agentId?: string,
  includeDataProducts: boolean = true,
  includeDataBoxTracking: boolean = true
): AgentMcpServer | null => {
  // Flat set of ALL currently-selected data product IDs (standalone + databox-nested combined)
  const currentDataProductIds = new Set<string>()

  // Skip entirely for SmartInsights (includeDataProducts = false)
  if (includeDataProducts) {
    // 1) Standalone data products: top-level cards in mixedAssetData, added directly to the
    // agent (not attached to any specific databox).
    mixedAssetData
      .filter((item) => item.type === 'dataproduct')
      .forEach((item) => {
        const id = String(item.id)
        if (id && id !== 'undefined' && id !== 'null') {
          currentDataProductIds.add(id)
        }
      })

    // 2) Databox-nested data products: for every selected databox, also look inside its `selectedItems`
    mixedAssetData
      .filter((item) => item.type === 'databox')
      .forEach((databox: any) => {
        ;(databox.selectedItems || [])
          .filter((selectedItem: any) => selectedItem.type === 'dataproduct')
          .forEach((selectedItem: any) => {
            // Use the product's real id (dataProductId), not its display/selection id
            const id = String(selectedItem.dataProductId ?? selectedItem.id)
            if (id && id !== 'undefined' && id !== 'null') {
              currentDataProductIds.add(id)
            }
          })
      })
  }

  // Whether this MCP server had any data on the originally-loaded agent (used only to
  // decide if a "clear" payload should still be sent when current data is now empty)
  const hadOriginalData = !!originalDataSourcesByMcp?.[mcpServerId]

  // Build dataProductScopes with ALL current IDs (single pass with Array.from)
  const dataProductScopes: DataProductScope[] | null =
    currentDataProductIds.size > 0 ? Array.from(currentDataProductIds, (id) => ({ id })) : null

  // Build dataProductGroups: data products grouped by the databox they belong to.
  // (null databoxId = standalone data product, added directly at the agent level)
  let dataProductGroups: AgentMcpServer['dataProductGroups']

  if (includeDataProducts) {
    // Map key = databoxId (or null for standalone), value = list of data product IDs in that group
    const dataProductGroupsMap = new Map<string | null, string[]>()

    const addToGroup = (databoxId: string | null, id: string) => {
      if (!id || id === 'undefined' || id === 'null') return
      // Create this group's array the first time we see this databoxId
      if (!dataProductGroupsMap.has(databoxId)) dataProductGroupsMap.set(databoxId, [])
      // Add the id to its group's array
      dataProductGroupsMap.get(databoxId)!.push(id)
    }

    // 1) Standalone data products (top-level cards, not attached to a databox) → group key `null`
    mixedAssetData
      .filter((item) => item.type === 'dataproduct')
      .forEach((item) => addToGroup(null, String(item.id)))

    // 2) Databox-nested data products (selected inside a databox's selectedItems) → group key
    // is that specific databox's id
    mixedAssetData
      .filter((item) => item.type === 'databox')
      .forEach((databox: any) => {
        const databoxId = String(databox.smartDocsDataboxId || databox.id)
        ;(databox.selectedItems || [])
          .filter((selectedItem: any) => selectedItem.type === 'dataproduct')
          .forEach((selectedItem: any) =>
            addToGroup(databoxId, String(selectedItem.dataProductId ?? selectedItem.id))
          )
      })

    // Convert the Map into the array shape the API payload expects
    dataProductGroups =
      dataProductGroupsMap.size > 0
        ? Array.from(dataProductGroupsMap, ([databoxId, dataProductIds]) => ({
            databoxId,
            dataProductIds
          }))
        : undefined
  }

  // Extract teamIdentifier from first databox (all databoxes in same agent share same team).
  // Only relevant for SmartInsights - Snowflake doesn't need it.
  const teamIdentifier = includeDataBoxTracking
    ? mixedAssetData.find((item) => item.type === 'databox')?.teamIdentifier
    : undefined

  // Ids of databoxes selected in their entirety (no specific items picked), deduplicated
  let fullySelectedDataboxIds: string[] | undefined
  if (includeDataProducts) {
    const idsSet = new Set<string>()
    mixedAssetData
      .filter((item) => item.type === 'databox' && !(item as any).selectedItems?.length)
      .forEach((item: any) => {
        const id = String(item.id)
        if (id && id !== 'undefined' && id !== 'null') {
          idsSet.add(id)
        }
      })
    fullySelectedDataboxIds = idsSet.size > 0 ? Array.from(idsSet) : undefined
  }

  // Nothing to send: this server has no current data, and nothing to clear either
  if (mixedAssetData.length === 0 && !hadOriginalData) {
    return null
  }

  return {
    ...(agentId && { agentId }),
    mcpServerId,
    ...(teamIdentifier && { teamIdentifier }),
    ...(dataProductScopes && { dataProductScopes }),
    ...(dataProductGroups && { dataProductGroups }),
    ...(fullySelectedDataboxIds && { fullySelectedDataboxIds })
  }
}

/**
 * Build agentMcpServers array for both SmartInsights and Snowflake MCP servers
 * Routing logic:
 * - Databoxes → both SmartInsights and Snowflake
 * - Data products → Snowflake only
 * @param mixedAssetData - Array of selected databoxes and data products from UI
 * @param smartInsightsMcpServerId - ID of the SmartInsights MCP server
 * @param snowflakeMcpServerId - ID of the Snowflake MCP server (optional)
 * @param originalDataSourcesByMcp - Original data source IDs grouped by MCP server (for edit mode)
 * @param agentId - The ID of the agent (optional for new agents)
 * @returns Array of AgentMcpServer objects for API payload
 */
export const buildAgentMcpServersArray = (
  mixedAssetData: MixedCardItem[],
  smartInsightsMcpServerId: string,
  snowflakeMcpServerId: string | null,
  webSearchMcpServerId: string | null,
  codeInterpreterMcpServerId: string | null,
  originalDataSourcesByMcp: OriginalDataSourcesByMcp | null,
  agentId?: string
): AgentMcpServer[] => {
  const result: AgentMcpServer[] = []

  // Separate databoxes and data products
  const databoxes = mixedAssetData.filter((item) => item.type === 'databox')
  const dataProducts = mixedAssetData.filter((item) => item.type === 'dataproduct')

  // Build SmartInsights MCP server payload (only databoxes, no data products)
  if (databoxes.length > 0 || originalDataSourcesByMcp?.[smartInsightsMcpServerId]) {
    const smartInsightsPayload = buildAgentMcpServersPayload(
      databoxes, // Only databoxes
      smartInsightsMcpServerId,
      originalDataSourcesByMcp,
      agentId,
      false // includeDataProducts - SmartInsights doesn't handle data products
    )
    if (smartInsightsPayload) {
      result.push(smartInsightsPayload)
    }
  }

  // Build Snowflake MCP server payload (both databoxes and data products)
  if (snowflakeMcpServerId) {
    const hasDataForSnowflake =
      databoxes.length > 0 ||
      dataProducts.length > 0 ||
      originalDataSourcesByMcp?.[snowflakeMcpServerId]

    if (hasDataForSnowflake) {
      const snowflakePayload = buildAgentMcpServersPayload(
        mixedAssetData, // Both databoxes and data products
        snowflakeMcpServerId,
        originalDataSourcesByMcp,
        agentId,
        true, // includeDataProducts
        false // includeDataBoxTracking - Snowflake doesn't need dataBoxIds/teamIdentifier
      )
      if (snowflakePayload) {
        result.push(snowflakePayload)
      }
    }
  }
  // Build WebSearch MCP server object
  if (webSearchMcpServerId) {
    const webSearchPayload = {
      mcpServerId: webSearchMcpServerId,
      ...(agentId && { agentId })
    }
    if (webSearchPayload) {
      result.push(webSearchPayload)
    }
  }

  // Build CodeInterpreter MCP server object
  if (codeInterpreterMcpServerId) {
    const codeInterpreterPayload = {
      mcpServerId: codeInterpreterMcpServerId,
      ...(agentId && { agentId })
    }
    if (codeInterpreterPayload) {
      result.push(codeInterpreterPayload)
    }
  }

  return result
}
